import React, { useState } from 'react';
import { Card, Typography, Spin, Empty, Button, Tag, Space, Modal, Form, Input, Upload, message } from 'antd';
import { 
  CalendarOutlined, 
  FileOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useClassAssignments, useCreateAssignmentSubmission, useUpdateAssignmentSubmission, useUserSubmission } from '../hooks/classAssignments';
import { useStudentCheck } from '../hooks/classFeedback';
import { useResponsive } from '../hooks/useResponsive';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import authService from '../services/authService';
import { uploadFile } from '../utils/fileApi';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ClassAssignments: React.FC = () => {
  const { isMobile } = useResponsive();
  const currentUser = authService.getCurrentUser();
  const userId = currentUser?.id || '';
  useSecurityProtection();

  // Get student's class information
  const { data: studentData, isLoading: isStudentLoading } = useStudentCheck(userId);
  const classId = studentData?.id;

  // Get assignments for the class
  const { data: assignments, isLoading: isAssignmentsLoading, error } = useClassAssignments(classId || '');

  // Submission modal state
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState<any>(null);
  const [form] = Form.useForm();

  // Create and update submission mutations
  const createSubmission = useCreateAssignmentSubmission();
  const updateSubmission = useUpdateAssignmentSubmission();

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  // Function to check if assignment is overdue
  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  // Function to get days until due date
  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Function to get assignment status
  const getAssignmentStatus = (dueDate: string) => {
    const daysUntil = getDaysUntilDue(dueDate);
    
    if (daysUntil < 0) {
      return {
        text: 'Overdue',
        color: 'red',
        icon: <ExclamationCircleOutlined />
      };
    } else if (daysUntil === 0) {
      return {
        text: 'Due Today',
        color: 'orange',
        icon: <ClockCircleOutlined />
      };
    } else if (daysUntil <= 3) {
      return {
        text: `${daysUntil} days left`,
        color: 'orange',
        icon: <ClockCircleOutlined />
      };
    } else {
      return {
        text: `${daysUntil} days left`,
        color: 'green',
        icon: <CheckCircleOutlined />
      };
    }
  };

  // Function to get assignment type color
  const getAssignmentTypeColor = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'homework': 'blue',
      'project': 'purple',
      'quiz': 'cyan',
      'exam': 'red',
      'presentation': 'magenta',
      'lab': 'geekblue',
      'assignment': 'green'
    };
    return typeMap[type.toLowerCase()] || 'default';
  };

  // Function to handle attachment download
  const handleDownload = (attachmentUrl: string, title: string) => {
    const link = document.createElement('a');
    link.href = attachmentUrl;
    link.download = `${title}_attachment`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to open submit modal
  const handleOpenSubmitModal = (assignment: any) => {
    setSelectedAssignment(assignment);
    setIsSubmitModalVisible(true);
    setIsEditMode(false);
    setEditingSubmission(null);
    form.resetFields();
    setUploadedFile(null);
  };

  // Function to open edit modal
  const handleOpenEditModal = (assignment: any, submission: any) => {
    setSelectedAssignment(assignment);
    setEditingSubmission(submission);
    setIsSubmitModalVisible(true);
    setIsEditMode(true);
    
    // Pre-fill form with existing submission data
    form.setFieldsValue({
      submission_text: submission.submission_text,
    });
    
    // Set existing file if available
    if (submission.submission_url) {
      setUploadedFile({
        name: 'Previously uploaded file',
        url: submission.submission_url,
      });
    } else {
      setUploadedFile(null);
    }
  };

  // Function to close submit modal
  const handleCloseSubmitModal = () => {
    setIsSubmitModalVisible(false);
    setSelectedAssignment(null);
    setIsEditMode(false);
    setEditingSubmission(null);
    form.resetFields();
    setUploadedFile(null);
  };

  // Function to handle file upload
  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const result = await uploadFile(file, 'assignment-submissions');
      setUploadedFile({
        name: file.name,
        url: result.url,
      });
      message.success('File uploaded successfully!');
      return false; // Prevent default upload behavior
    } catch (error) {
      message.error(`Failed to upload file: ${(error as Error).message}`);
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  // Function to handle submission
  const handleSubmit = async (values: any) => {
    try {
      if (isEditMode && editingSubmission) {
        // Update existing submission
        const updateData = {
          submission_text: values.submission_text,
          submission_url: uploadedFile?.url || editingSubmission.submission_url,
          status: 'pending',
        };

        await updateSubmission.mutateAsync({
          submissionId: editingSubmission.id,
          data: updateData,
        });
        message.success('Submission updated successfully!');
      } else {
        // Create new submission
        const submissionData = {
          assignment_id: selectedAssignment.id,
          user_id: parseInt(userId),
          submission_text: values.submission_text,
          submission_url: uploadedFile?.url || '',
          status: 'pending',
        };

        await createSubmission.mutateAsync(submissionData);
        message.success('Assignment submitted successfully!');
      }
      
      handleCloseSubmitModal();
    } catch (error) {
      message.error(`Failed to ${isEditMode ? 'update' : 'submit'} assignment: ${(error as Error).message}`);
    }
  };

  if (isStudentLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip="Loading class information..." />
      </div>
    );
  }

  if (!classId) {
    return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '16px' : '24px'
      }}>
        <Card>
          <Empty
            description="You are not enrolled in any class"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      </div>
    );
  }

  if (isAssignmentsLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip="Loading assignments..." />
      </div>
    );
  }

  // Sort assignments by due date (closest first)
  const sortedAssignments = assignments ? [...assignments].sort((a, b) => 
    new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
  ) : [];

  // Component to render individual assignment card
  const AssignmentCard: React.FC<{ assignment: any }> = ({ assignment }) => {
    const status = getAssignmentStatus(assignment.due_date);
    const isLate = isOverdue(assignment.due_date);
    const { data: submission } = useUserSubmission(assignment.id, userId);
    
    const isLateAndNotSubmitted = isLate && !submission;

    return (
      <Card
        key={assignment.id}
        hoverable
        style={{
          borderRadius: '12px',
          border: isLateAndNotSubmitted ? '2px solid #ff4d4f' : '1px solid #e8e8e8',
          boxShadow: isLateAndNotSubmitted 
            ? '0 4px 12px rgba(255, 77, 79, 0.15)' 
            : '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease',
          background: isLateAndNotSubmitted ? '#fff2f0' : '#fff'
        }}
        bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Title level={4} style={{ 
                margin: '0 0 8px 0',
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '600',
                color: isLateAndNotSubmitted ? '#cf1322' : '#262626'
              }}>
                {assignment.title}
              </Title>
              
              <Space size={isMobile ? "small" : "middle"} wrap>
                <Tag 
                  color={getAssignmentTypeColor(assignment.assignment_type)}
                  style={{ 
                    fontSize: isMobile ? '12px' : '13px',
                    padding: '2px 12px',
                    borderRadius: '6px'
                  }}
                >
                  {assignment.assignment_type}
                </Tag>

                <Tag 
                  icon={status.icon}
                  color={status.color}
                  style={{ 
                    fontSize: isMobile ? '12px' : '13px',
                    padding: '2px 12px',
                    borderRadius: '6px'
                  }}
                >
                  {status.text}
                </Tag>
              </Space>
            </div>

            <div style={{ 
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <Text strong style={{ 
                fontSize: isMobile ? '14px' : '16px',
                color: '#1890ff'
              }}>
                Max Score: {assignment.max_score}
              </Text>
            </div>
          </div>

          {/* Description */}
          {assignment.description && (
            <Paragraph 
              style={{ 
                margin: 0,
                fontSize: isMobile ? '14px' : '15px',
                color: '#595959',
                lineHeight: '1.6'
              }}
              ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
            >
              {assignment.description}
            </Paragraph>
          )}

          {/* Submission Status */}
          <SubmissionStatusCard assignmentId={assignment.id} assignment={assignment} />

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '12px',
            borderTop: '1px solid #f0f0f0',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Space direction="vertical" size={4}>
              <Space size="small">
                <CalendarOutlined style={{ color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                  Due: {formatDate(assignment.due_date)}
                </Text>
              </Space>
              
              {assignment.attachment_url && (
                <Space size="small">
                  <FileOutlined style={{ color: '#8c8c8c' }} />
                  <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                    Attachment available
                  </Text>
                </Space>
              )}
            </Space>

            <Space size={isMobile ? "small" : "middle"}>
              {assignment.attachment_url && (
                <Button
                  icon={<DownloadOutlined />}
                  size={isMobile ? 'small' : 'middle'}
                  onClick={() => handleDownload(assignment.attachment_url!, assignment.title)}
                  style={{
                    borderRadius: '8px'
                  }}
                >
                  {!isMobile && 'Attachment'}
                </Button>
              )}
              
              <Button
                type="primary"
                size={isMobile ? 'small' : 'middle'}
                disabled={isLate || !!submission}
                onClick={() => handleOpenSubmitModal(assignment)}
                style={{
                  borderRadius: '8px',
                  background: (isLate || !!submission)
                    ? '#d9d9d9'
                    : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  border: 'none',
                  cursor: (isLate || !!submission) ? 'not-allowed' : 'pointer',
                  opacity: (isLate || !!submission) ? 0.6 : 1
                }}
              >
                {submission 
                  ? 'Submitted' 
                  : isLate 
                  ? 'Overdue' 
                  : 'Submit'}
              </Button>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  // Component to render submission status
  const SubmissionStatusCard: React.FC<{ assignmentId: number; assignment: any }> = ({ assignmentId, assignment }) => {
    const { data: submission, isLoading } = useUserSubmission(assignmentId, userId);


    if (isLoading) {
      return <Spin size="small" />;
    }
    
    if (!submission) {
      return null;
    }
    
    const getStatusColor = (status: string) => {
      const statusMap: { [key: string]: string } = {
        'pending': 'orange',
        'graded': 'green',
        'success': 'green',
        'rejected': 'red'
      };
      return statusMap[status.toLowerCase()] || 'default';
    };

    const getStatusText = (status: string) => {
      const statusMap: { [key: string]: string } = {
        'pending': 'Under Review',
        'graded': 'Graded',
        'success': 'Graded',
        'rejected': 'Returned'
      };
      return statusMap[status.toLowerCase()] || status;
    };

    return (
      <Card
        size="small"
        style={{
          marginTop: '12px',
          background: submission.status === 'graded' || submission.status === 'success' 
            ? '#f6ffed' 
            : submission.status === 'rejected'
            ? '#fff2f0'
            : '#fffbe6',
          border: `1px solid ${
            submission.status === 'graded' || submission.status === 'success'
            ? '#b7eb8f' 
            : submission.status === 'rejected'
            ? '#ffccc7'
            : '#ffe58f'
          }`,
          borderRadius: '8px'
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <CheckCircleOutlined style={{ 
                color: submission.status === 'graded' || submission.status === 'success'
                  ? '#52c41a' 
                  : submission.status === 'rejected'
                  ? '#ff4d4f'
                  : '#faad14',
                fontSize: '16px'
              }} />
              <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>
                Submission Status
              </Text>
            </Space>
            <Tag color={getStatusColor(submission.status)}>
              {getStatusText(submission.status)}
            </Tag>
          </div>

          <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '13px' }}>
            Submitted: {formatDate(submission.submitted_at)}
          </Text>

          {submission.score !== null && submission.score !== undefined && (
            <div>
              <Text strong style={{ fontSize: isMobile ? '13px' : '14px', color: '#1890ff' }}>
                Score: {submission.score}
              </Text>
            </div>
          )}

          {submission.feedback && (
            <div style={{ 
              padding: '8px 12px', 
              background: 'white', 
              borderRadius: '6px',
              border: '1px solid #f0f0f0'
            }}>
              <Text strong style={{ fontSize: isMobile ? '12px' : '13px', display: 'block', marginBottom: '4px' }}>
                Feedback:
              </Text>
              <Text style={{ fontSize: isMobile ? '12px' : '13px', color: '#595959' }}>
                {submission.feedback}
              </Text>
            </div>
          )}

          <Space size="small" style={{ width: '100%' }}>
            {submission.submission_url && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => window.open(submission.submission_url, '_blank')}
                style={{ borderRadius: '6px' }}
              >
                View Submission
              </Button>
            )}
            
            {submission.status === 'pending' && (
              <Button
                size="small"
                type="primary"
                disabled={isOverdue(assignment.due_date) || submission.status !== 'pending'}
                onClick={() => handleOpenEditModal(assignment, submission)}
                style={{ borderRadius: '6px' }}
              >
                Edit Submission
              </Button>
            )}
          </Space>
        </Space>
      </Card>
    );
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px',
      background: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
        <Title level={2} style={{ 
          margin: 0, 
          fontSize: isMobile ? '20px' : '28px',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          Class Assignments
        </Title>
        {studentData?.class_title && (
          <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
            Class: {studentData.class_title}
          </Text>
        )}
      </div>

      {error ? (
        <Card>
          <Empty
            description="Failed to load assignments. Please try again later."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : !sortedAssignments || sortedAssignments.length === 0 ? (
        <Card>
          <Empty
            description="No assignments available yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: isMobile ? '16px' : '20px'
        }}>
          {sortedAssignments.map((assignment) => (
            <AssignmentCard key={assignment.id} assignment={assignment} />
          ))}
        </div>
      )}

      {/* Submission Modal */}
      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            {isEditMode ? 'Edit Submission' : 'Submit Assignment'}
          </Title>
        }
        open={isSubmitModalVisible}
        onCancel={handleCloseSubmitModal}
        footer={null}
        width={isMobile ? '100%' : 600}
        centered
      >
        {selectedAssignment && (
          <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
            <Text strong style={{ fontSize: '16px', color: '#262626' }}>
              {selectedAssignment.title}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Due: {formatDate(selectedAssignment.due_date)}
            </Text>
            {isOverdue(selectedAssignment.due_date) && (
              <>
                <br />
                <Text type="danger" strong style={{ fontSize: '14px' }}>
                  ⚠️ This assignment is overdue
                </Text>
              </>
            )}
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="submission_text"
            label="Submission Description"
            rules={[
              { required: true, message: 'Please provide a description of your submission' },
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Describe your solution or provide details about your submission..."
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            label="Upload File (Optional)"
            help="You can upload supporting documents, PDFs, images, or any relevant files"
          >
            <Upload
              beforeUpload={(file) => {
                handleFileUpload(file);
                return false;
              }}
              maxCount={1}
              onRemove={() => setUploadedFile(null)}
              fileList={uploadedFile ? [{
                uid: '-1',
                name: uploadedFile.name,
                status: 'done',
                url: uploadedFile.url,
              }] : []}
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={isUploading}
                style={{ borderRadius: '8px' }}
              >
                {isUploading ? 'Uploading...' : 'Click to Upload'}
              </Button>
            </Upload>
            {uploadedFile && (
              <Text type="success" style={{ marginTop: '8px', display: 'block' }}>
                ✓ File uploaded successfully: {uploadedFile.name}
              </Text>
            )}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                onClick={handleCloseSubmitModal}
                style={{ borderRadius: '8px' }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createSubmission.isPending}
                style={{
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                  border: 'none'
                }}
              >
                {isEditMode ? 'Update Submission' : 'Submit Assignment'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClassAssignments;
